import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ExpandMore,
  Code,
  Api,
  Security,
  Speed,
  Chat,
  Dashboard,
  People,
  Settings,
  Analytics,
  Notifications,
  ExtensionOutlined,
  WebhookOutlined,
  AutoFixHigh,
  Support,
  School,
  Article,
  Launch,
} from "@mui/icons-material";

const DocumentationPage: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | false>(
    "overview",
  );

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedSection(isExpanded ? panel : false);
    };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
          Converse Documentation
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Complete guide to using Converse - Your comprehensive customer support
          platform
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          Welcome to Converse! This documentation will help you get the most out
          of our customer support platform.
        </Alert>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 20 }}>
            <CardHeader title="Quick Navigation" />
            <CardContent>
              <List>
                <ListItem button onClick={() => setExpandedSection("overview")}>
                  <ListItemIcon>
                    <Article />
                  </ListItemIcon>
                  <ListItemText primary="Overview" />
                </ListItem>
                <ListItem button onClick={() => setExpandedSection("features")}>
                  <ListItemIcon>
                    <AutoFixHigh />
                  </ListItemIcon>
                  <ListItemText primary="Features" />
                </ListItem>
                <ListItem
                  button
                  onClick={() => setExpandedSection("getting-started")}
                >
                  <ListItemIcon>
                    <School />
                  </ListItemIcon>
                  <ListItemText primary="Getting Started" />
                </ListItem>
                <ListItem button onClick={() => setExpandedSection("api")}>
                  <ListItemIcon>
                    <Api />
                  </ListItemIcon>
                  <ListItemText primary="API Reference" />
                </ListItem>
                <ListItem
                  button
                  onClick={() => setExpandedSection("integrations")}
                >
                  <ListItemIcon>
                    <ExtensionOutlined />
                  </ListItemIcon>
                  <ListItemText primary="ExtensionOutlineds" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper>
            <Accordion
              expanded={expandedSection === "overview"}
              onChange={handleAccordionChange("overview")}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Article sx={{ mr: 1 }} />
                  <Typography variant="h6">Platform Overview</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="body1" paragraph>
                    Converse is a modern, full-featured customer support
                    platform designed to help businesses provide exceptional
                    customer service. Built with React and TypeScript on the
                    frontend and FastAPI with Python on the backend, Converse
                    offers a comprehensive suite of tools for customer
                    communication, team collaboration, and service analytics.
                  </Typography>

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    What makes Converse different?
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Speed color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Lightning Fast"
                        secondary="Built on modern technologies for optimal performance"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Security color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Enterprise Security"
                        secondary="Advanced security features with role-based access control"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ExtensionOutlined color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Seamless ExtensionOutlineds"
                        secondary="Connect with popular tools like Slack, webhooks, and more"
                      />
                    </ListItem>
                  </List>

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Technology Stack
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="primary">
                            Frontend
                          </Typography>
                          <List dense>
                            <ListItem disablePadding>
                              <ListItemText primary="React 18" />
                            </ListItem>
                            <ListItem disablePadding>
                              <ListItemText primary="TypeScript" />
                            </ListItem>
                            <ListItem disablePadding>
                              <ListItemText primary="Material-UI" />
                            </ListItem>
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="primary">
                            Backend
                          </Typography>
                          <List dense>
                            <ListItem disablePadding>
                              <ListItemText primary="FastAPI" />
                            </ListItem>
                            <ListItem disablePadding>
                              <ListItemText primary="Python 3.12+" />
                            </ListItem>
                            <ListItem disablePadding>
                              <ListItemText primary="SQLAlchemy ORM" />
                            </ListItem>
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSection === "features"}
              onChange={handleAccordionChange("features")}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AutoFixHigh sx={{ mr: 1 }} />
                  <Typography variant="h6">Core Features</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardHeader
                        avatar={<Chat color="primary" />}
                        title="Conversations"
                        subheader="Unified inbox for all customer communications"
                      />
                      <CardContent>
                        <List dense>
                          <ListItem disablePadding>
                            <ListItemText primary="Multi-channel support (email, chat, social)" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Real-time messaging with WebSocket" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="File attachments and rich media" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Conversation threading and labels" />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardHeader
                        avatar={<People color="primary" />}
                        title="Team Management"
                        subheader="Organize your support team effectively"
                      />
                      <CardContent>
                        <List dense>
                          <ListItem disablePadding>
                            <ListItemText primary="Role-based access control" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Team assignments and workload distribution" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Agent capacity management" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Performance analytics" />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardHeader
                        avatar={<Analytics color="primary" />}
                        title="Analytics & Reporting"
                        subheader="Insights to improve your support operations"
                      />
                      <CardContent>
                        <List dense>
                          <ListItem disablePadding>
                            <ListItemText primary="Response time metrics" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Customer satisfaction surveys" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Agent performance dashboards" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Custom report generation" />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardHeader
                        avatar={<Support color="primary" />}
                        title="Support Tools"
                        subheader="Advanced tools to enhance productivity"
                      />
                      <CardContent>
                        <List dense>
                          <ListItem disablePadding>
                            <ListItemText primary="Canned responses library" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="SLA management and tracking" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Business hours configuration" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Pre-chat forms and campaigns" />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSection === "getting-started"}
              onChange={handleAccordionChange("getting-started")}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <School sx={{ mr: 1 }} />
                  <Typography variant="h6">Getting Started</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Quick Setup Guide
                  </Typography>

                  <Alert severity="success" sx={{ mb: 3 }}>
                    Follow these steps to get your Converse instance up and
                    running in minutes!
                  </Alert>

                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ mt: 3, fontWeight: "bold" }}
                  >
                    1. Account Setup
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Configure your account settings in Settings > Account Settings" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Set your domain, support email, and timezone" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Upload your company logo and favicon" />
                    </ListItem>
                  </List>

                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ mt: 3, fontWeight: "bold" }}
                  >
                    2. Team Configuration
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Add team members and assign roles" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Configure business hours and working schedules" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Set up agent capacity limits" />
                    </ListItem>
                  </List>

                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ mt: 3, fontWeight: "bold" }}
                  >
                    3. Channel Setup
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Configure your communication channels (email, chat, social)" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Set up webhook integrations" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Customize pre-chat forms" />
                    </ListItem>
                  </List>

                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ mt: 3, fontWeight: "bold" }}
                  >
                    4. Start Supporting Customers
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Create canned responses for common questions" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Set up SLA policies" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Begin handling conversations" />
                    </ListItem>
                  </List>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSection === "api"}
              onChange={handleAccordionChange("api")}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Api sx={{ mr: 1 }} />
                  <Typography variant="h6">API Reference</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Converse provides a comprehensive REST API for integrating
                    with external systems.
                  </Alert>

                  <Typography variant="h6" gutterBottom>
                    Authentication
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    All API requests require authentication using Bearer tokens.
                  </Typography>

                  <Box sx={{ my: 3 }}>
                    <Card
                      variant="outlined"
                      sx={{ backgroundColor: "#f5f5f5" }}
                    >
                      <CardContent>
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {`Authorization: Bearer your_api_token_here`}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>

                  <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                    Core Endpoints
                  </Typography>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Endpoint</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Method</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Description</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <code>
                              /{process.env.REACT_APP_API_PATH}/conversations
                            </code>
                          </TableCell>
                          <TableCell>
                            <Chip label="GET" color="success" size="small" />
                          </TableCell>
                          <TableCell>List all conversations</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <code>
                              /{process.env.REACT_APP_API_PATH}/conversations
                            </code>
                          </TableCell>
                          <TableCell>
                            <Chip label="POST" color="primary" size="small" />
                          </TableCell>
                          <TableCell>Create new conversation</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <code>
                              /{process.env.REACT_APP_API_PATH}/messages
                            </code>
                          </TableCell>
                          <TableCell>
                            <Chip label="POST" color="primary" size="small" />
                          </TableCell>
                          <TableCell>Send message</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <code>
                              /{process.env.REACT_APP_API_PATH}/contacts
                            </code>
                          </TableCell>
                          <TableCell>
                            <Chip label="GET" color="success" size="small" />
                          </TableCell>
                          <TableCell>List contacts</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <code>
                              /{process.env.REACT_APP_API_PATH}/support/ticket
                            </code>
                          </TableCell>
                          <TableCell>
                            <Chip label="POST" color="primary" size="small" />
                          </TableCell>
                          <TableCell>Create support ticket</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <code>
                              /{process.env.REACT_APP_API_PATH}/support/demo
                            </code>
                          </TableCell>
                          <TableCell>
                            <Chip label="POST" color="primary" size="small" />
                          </TableCell>
                          <TableCell>Schedule demo booking</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      startIcon={<Launch />}
                      href="/docs"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Full API Documentation
                    </Button>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSection === "integrations"}
              onChange={handleAccordionChange("integrations")}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ExtensionOutlined sx={{ mr: 1 }} />
                  <Typography variant="h6">ExtensionOutlineds</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardHeader
                        title="Slack ExtensionOutlined"
                        subheader="Connect your Slack workspace"
                      />
                      <CardContent>
                        <Typography variant="body2" paragraph>
                          Receive notifications and manage conversations
                          directly from Slack.
                        </Typography>
                        <List dense>
                          <ListItem disablePadding>
                            <ListItemText primary="Real-time notifications" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Slash commands support" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Two-way message sync" />
                          </ListItem>
                        </List>
                        <Button size="small" variant="outlined" sx={{ mt: 2 }}>
                          Configure Slack
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardHeader
                        title="Webhook ExtensionOutlined"
                        subheader="Custom webhook endpoints"
                      />
                      <CardContent>
                        <Typography variant="body2" paragraph>
                          Send real-time data to external systems via webhooks.
                        </Typography>
                        <List dense>
                          <ListItem disablePadding>
                            <ListItemText primary="Conversation events" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Message notifications" />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText primary="Custom payloads" />
                          </ListItem>
                        </List>
                        <Button size="small" variant="outlined" sx={{ mt: 2 }}>
                          Setup Webhooks
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, p: 3, backgroundColor: "#f8f9fa", borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Need Help?
        </Typography>
        <Typography variant="body1" paragraph>
          Our support team is here to help you get the most out of Converse.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button variant="contained" color="primary">
            Contact Support
          </Button>
          <Button variant="outlined">Schedule Demo</Button>
          <Button variant="text" startIcon={<Launch />}>
            Community Forum
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DocumentationPage;
